import * as React from "react";
import { observer } from "mobx-react";

import { useLocalization } from "@shared-ui/localization-context";
import { ViewMedium, Viewport, ViewSmall } from "@shared-ui/viewport-context";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import {
  UitkLayoutGrid,
  UitkLayoutGridItem,
  UitkLayoutConditionalGridColumnRow,
  UitkLayoutConditionalGridSpan,
} from "uitk-react-layout-grid";
import { UitkText } from "uitk-react-text";

import { MultiLOBPackageWizardContext } from "src/components/flexComponents/PageLevelProviders/WizardContextProvider/MultiLOBPackageWizardContext";

import { LobTitle } from "components/shared/LobTitle/LobTitle";
import { Location } from "components/shared/Location/Location";
import { Dates } from "components/shared/Dates/Dates";
import { Travelers } from "components/shared/Travelers/Travelers";

import { WizardHotelPWAFilter } from "components/flexComponents/WizardHotelPWA/components/WizardHotelPWAFilter";
import { LobSubtitle } from "components/shared/LobSubtitle/LobSubtitle";
import { SubmitRow } from "components/shared/StorefrontWizard/SubmitRow/SubmitRow";
import { LobErrorSummary } from "components/shared/LobErrorSummary/LobErrorSummary";
import { locKeys } from "components/flexComponents/WizardHotelPWA/components/l10n";
import { isVariantEnabled, Experiment } from "components/utility/experiment";

import { HotelAddFlightCheckbox } from "../components/HotelAddFlightCheckbox";
import { HotelAddCarCheckbox } from "../components/HotelAddCarCheckbox";
import { HotelHiddenSearchData } from "../components/HotelHiddenSearchData/HotelHiddenSearchData";
import { HotelPackageRatesTermsCheckbox } from "../components/HotelPackageRatesTermsCheckbox";
import { MultiplePlaces } from "../components/MultiplePlaces";

import { TravelersTriggerTypes } from "components/shared/TravelersField/typings";
import { PackagesSubLobs, HotelRatesType } from "stores/wizard/state/typings";
import { WizardHotelPWAv2Props, WizardHotelPWAv2FTNodeOptions } from "../typings";
import { Columns } from "components/shared/StorefrontWizard/typings";
import { HotelSubTabs } from "../components/HotelSubTabs";
import { Action } from "src/components/utility/analytics/FlexAnalyticsUtils";
import { normalizeOptionsData } from "src/components/flexFramework/FlexTemplateNode";
import { getLocError, onAddFlightCheckboxChange, NO_REHYDRATATION_VALUES } from "../utils/WizardHotelPWAv2Utils";

export const WizardHotelPWAv2View: React.FunctionComponent<WizardHotelPWAv2Props> = observer((props) => {
  const { formatText } = useLocalization();
  const {
    globalWizardState,
    hotelWizardState,
    hotelWizardState: { config, numberOfErrors, errorInputRef, moreThanOneError },
  } = props.wizardState;
  const { flexTemplateNode, flexModuleModelStore, page, context } = props;
  const model = flexModuleModelStore.getModel(flexTemplateNode.id);
  const options = normalizeOptionsData(flexTemplateNode, model) as WizardHotelPWAv2FTNodeOptions;

  const {
    elementId,
    enableAddFlight,
    enableAddCar,
    enablePackageRates,
    percentSave,
    packageRatesTermsUrl,
    enableMultiplePlaces,
    desktopAutoOpenCalendar,
    showDestinationField,
  } = config;
  const isBiasedWizardEmpty = enablePackageRates ? true : hotelWizardState.config?.isBiasedWizardEmpty;
  const displayTravelersInline = enablePackageRates
    ? false
    : !isBiasedWizardEmpty
    ? true
    : isVariantEnabled(context, Experiment.Alignment_of_Traveler_Input);
  const { halfWizard } = globalWizardState.config;

  const locationColumns: Columns = {
    fullWidth: {
      small: 2,
      large: displayTravelersInline ? 7 : 5,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const datesColumns: Columns = {
    fullWidth: {
      small: 2,
      large: displayTravelersInline ? 5 : 4,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const travelersColumns: Columns = {
    fullWidth: {
      small: 2,
      large: 3,
    },
    half: {
      small: 2,
      large: 2,
    },
  };
  const addFlightColumn: Columns = {
    fullWidth: {
      small: 1,
      large: 2,
    },
    half: {
      small: 1,
      large: 4,
    },
  };
  const addCarColumn: Columns = {
    fullWidth: {
      small: 1,
      large: 2,
    },
    half: {
      small: 1,
      large: 4,
    },
  };
  const multiplePlacesColumn: Columns = {
    fullWidth: {
      small: 2,
      large: 3,
    },
    half: {
      small: 2,
      large: 12,
    },
  };
  const mainGridColumns: UitkLayoutConditionalGridColumnRow = { small: 2, large: 12 };
  const datesColSpan = datesColumns[halfWizard ? "half" : "fullWidth"];
  const locationColSpan = locationColumns[halfWizard ? "half" : "fullWidth"];
  const travelersColSpan: UitkLayoutConditionalGridSpan = travelersColumns[halfWizard ? "half" : "fullWidth"];
  const addFlightColSpan: UitkLayoutConditionalGridSpan = addFlightColumn[halfWizard ? "half" : "fullWidth"];
  const addCarColSpan: UitkLayoutConditionalGridSpan = addCarColumn[halfWizard ? "half" : "fullWidth"];
  const multiplePlacesColSpan: UitkLayoutConditionalGridSpan = multiplePlacesColumn[halfWizard ? "half" : "fullWidth"];
  const hotelFiltersColSpan: UitkLayoutConditionalGridSpan = { small: 2, large: 8 };

  const isMultiplePlacesDisabled = hotelWizardState.isAddACarChecked || hotelWizardState.isAddAFlightChecked;
  const wizardStateAddFlight = hotelWizardState.isAddAFlightChecked;

  const [isAddFlightActive, updateAddAFlight] = React.useState(wizardStateAddFlight);

  React.useEffect(() => {
    updateAddAFlight(wizardStateAddFlight);
  }, [wizardStateAddFlight]);

  const disclaimerMultiplePlacesLocKey = isMultiplePlacesDisabled
    ? hotelWizardState.config.useDialogTextWithoutCar
      ? locKeys.dialogTextWithoutCar
      : locKeys.dialogText
    : "";

  const [autoOpenCalendar, setAutoOpenCalendar] = React.useState<boolean>(Boolean(desktopAutoOpenCalendar));

  const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
    hotelWizardState.wizardInputsArray.push(ref);
    if (secondRef) {
      hotelWizardState.wizardInputsArray.push(secondRef);
    }
  };

  const updatePackagesOnReflection = () => {
    const packageState = props.wizardState.packageWizardState;
    const { isAddAFlightChecked, isAddACarChecked } = hotelWizardState;

    if (isAddACarChecked && isAddAFlightChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.FLIGHT_HOTEL_CAR);
    } else if (isAddACarChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.HOTEL_CAR);
    } else if (isAddAFlightChecked) {
      packageState.updateSubLOBState(PackagesSubLobs.FLIGHT_HOTEL);
    }
  };

  const toggleDatePicker = () => {
    setAutoOpenCalendar(!autoOpenCalendar);
  };

  const getLayoutTravelersInline = (alignment: "end" | "start") => (
    <UitkLayoutFlex data-testid="travelers-inline-link" alignItems={alignment} justifyContent={alignment} space="six">
      <>
        <UitkLayoutFlexItem alignSelf={alignment}>
          <Travelers
            lobState={hotelWizardState}
            colSpan={travelersColSpan}
            noRehydratationValues={NO_REHYDRATATION_VALUES}
            triggerType={TravelersTriggerTypes.LINK_BUTTON}
          />
        </UitkLayoutFlexItem>
      </>
    </UitkLayoutFlex>
  );

  const formElements = {
    main: {
      errorSummary: (
        <LobErrorSummary
          locHeadingToken={getLocError(false, hotelWizardState)!}
          locHeadingArgs={moreThanOneError() && [numberOfErrors]}
          locLinkToken={getLocError(true, hotelWizardState)}
          linkClickFocusId="roundtrip-error"
          inputErrorRef={errorInputRef}
          colSpan={mainGridColumns}
          setInputsRefs={setInputsRefs}
        />
      ),
      destinationField: (
        <Location
          originField={false}
          destinationField
          lobState={hotelWizardState}
          colSpan={locationColSpan}
          setInputsRefs={setInputsRefs}
          globalConfig={globalWizardState.config}
          onClick={props.destinationOnClick}
        />
      ),
      dates: (
        <Dates
          lobState={hotelWizardState}
          colSpan={datesColSpan}
          setInputsRefs={setInputsRefs}
          autoOpenCalendar={autoOpenCalendar}
          toggleDatePicker={toggleDatePicker}
        />
      ),
      travelers: (
        <Travelers
          lobState={hotelWizardState}
          colSpan={travelersColSpan}
          noRehydratationValues={NO_REHYDRATATION_VALUES}
        />
      ),
    },
    addFlight: {
      flightCheckbox: (
        <UitkLayoutGridItem colSpan={addFlightColSpan}>
          <div>
            <HotelAddFlightCheckbox
              hotelWizardState={hotelWizardState}
              onChange={onAddFlightCheckboxChange}
              updatePackageOnReflection={updatePackagesOnReflection}
              analytics={props.analytics}
            />
          </div>
        </UitkLayoutGridItem>
      ),
      flightLocation: (
        <UitkLayoutGrid columns={mainGridColumns}>
          <UitkLayoutGridItem colSpan={locationColSpan}>
            <div>
              <Location
                originField
                destinationField={false}
                lobState={hotelWizardState}
                setInputsRefs={setInputsRefs}
              />
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      ),
    },
    addCar: {
      addCarCheckbox: (
        <UitkLayoutGridItem colSpan={addCarColSpan}>
          <div>
            <HotelAddCarCheckbox
              hotelWizardState={hotelWizardState}
              updatePackageOnReflection={updatePackagesOnReflection}
              analytics={props.analytics}
            />
          </div>
        </UitkLayoutGridItem>
      ),
    },
    showFilters: {
      hotelFilters: (
        <UitkLayoutGrid columns={mainGridColumns}>
          <UitkLayoutGridItem colSpan={hotelFiltersColSpan}>
            <div>
              <WizardHotelPWAFilter
                changeFormProperties={hotelWizardState.changeFormFilterProperties}
                checkboxFilterChecks={hotelWizardState.checkboxFilterChecks}
                advancedOptionsFilterVisible={hotelWizardState.advancedOptionsFilterVisible}
              />
            </div>
          </UitkLayoutGridItem>
        </UitkLayoutGrid>
      ),
    },
    showPackageRates: {
      packageRatesCheckbox: (
        <HotelPackageRatesTermsCheckbox
          hotelWizardState={hotelWizardState}
          percentSave={percentSave}
          packageRatesTermsUrl={packageRatesTermsUrl}
          isChecked={hotelWizardState.isPackageRatesTermsChecked}
        />
      ),
    },
    showMultiplePlaces: {
      multiplePlacesCheckbox: (
        <MultiplePlaces
          hotelWizardState={hotelWizardState}
          disabled={isMultiplePlacesDisabled}
          isChecked={hotelWizardState.isMultiplePlacesChecked}
          colSpan={multiplePlacesColSpan}
        />
      ),
      multiplePlacesDiscalimer: (
        <UitkLayoutGridItem colSpan={mainGridColumns}>
          <div aria-live="polite">
            <UitkText size={300}>{formatText(disclaimerMultiplePlacesLocKey)}</UitkText>
          </div>
        </UitkLayoutGridItem>
      ),
    },
  };

  const renderMainFormContent = (isInsideMultiLOBPackageWizard: boolean, isPackageRatesSubTab: boolean) => (
    <>
      <UitkLayoutGrid columns={mainGridColumns} space="three" className="all-t-margin-three">
        {numberOfErrors > 0 && formElements.main.errorSummary}
        {showDestinationField && formElements.main.destinationField}
        {formElements.main.dates}
        {!displayTravelersInline && isBiasedWizardEmpty && formElements.main.travelers}

        {!isPackageRatesSubTab && (
          <>
            {enableMultiplePlaces &&
              isMultiplePlacesDisabled &&
              formElements.showMultiplePlaces.multiplePlacesDiscalimer}
            {enableAddFlight && !isInsideMultiLOBPackageWizard && formElements.addFlight.flightCheckbox}
            {enableAddCar && !isInsideMultiLOBPackageWizard && formElements.addCar.addCarCheckbox}
            {enableMultiplePlaces && formElements.showMultiplePlaces.multiplePlacesCheckbox}
          </>
        )}
      </UitkLayoutGrid>

      {isPackageRatesSubTab && formElements.showPackageRates.packageRatesCheckbox}

      {!isPackageRatesSubTab && (
        <>
          {isAddFlightActive && formElements.addFlight.flightLocation}
          {hotelWizardState.config.includeFiltersHotelPWAWizard && formElements.showFilters.hotelFilters}
        </>
      )}
    </>
  );

  const currentSubTab = hotelWizardState.hotelTypeSubTab;
  const handlePackageRatesSubTabChange = (hotelRateType: HotelRatesType) => {
    props.wizardState.hotelWizardState.updateHotelTypeSubTab(hotelRateType);
  };

  const pageName = page ? page.pageName : "unknown";
  const moduleName = halfWizard ? "HalfBiasedWizard" : "FullBiasedWizard";
  const linkName = "HotelSearch";
  const sectionName = "SQM";
  const rfrrid = `${pageName}.${moduleName}.${linkName}.${sectionName}`;

  const extraTracking: any = {
    schemaName: "referrer",
    messageContent: {
      referrerId: rfrrid,
      eventType: Action.CLICK,
    },
  };

  const pageHeading = props.compositionStore?.composition?.pageHeading;
  const pageSubHeadline = props.compositionStore?.composition?.pageSubHeadline;

  const titleFromComposition = (options.titleFromPageHeader && pageHeading) || undefined;
  const subTitleFromComposition = (options.subTitleFromPageSubHeader && pageSubHeadline) || undefined;

  return (
    <form
      noValidate
      className="WizardHotelPWAv2"
      id={elementId}
      data-fm={options.fmId}
      data-fm-title-id={options.fmTitleId}
      onSubmit={hotelWizardState.submit}
      action={hotelWizardState.formAction}
    >
      <MultiLOBPackageWizardContext.Consumer>
        {({ isInsideMultiLOBPackageWizard }) => {
          if (isInsideMultiLOBPackageWizard) {
            props.wizardState.hotelWizardState.toggleMultiplePlacesCheckbox({ stateToForce: false });
          }

          return (
            <>
              {hotelWizardState.config.heading.fixedTitle && (
                <LobTitle lobState={hotelWizardState} customTitle={titleFromComposition} />
              )}
              <LobSubtitle lobState={hotelWizardState} customSubTitle={subTitleFromComposition} />

              {displayTravelersInline && (
                <Viewport>
                  <ViewSmall>{getLayoutTravelersInline("start")}</ViewSmall>
                  <ViewMedium>{getLayoutTravelersInline("end")}</ViewMedium>
                </Viewport>
              )}

              {/* with package rates ON render the grid inside the tabs */}
              {enablePackageRates && (
                <HotelSubTabs
                  currentSubTab={currentSubTab}
                  onTabSelect={handlePackageRatesSubTabChange}
                  renderTabChildren={renderMainFormContent}
                />
              )}

              {/* with package rates OFF render the grid directly as there are no tabs */}
              {!enablePackageRates && renderMainFormContent(isInsideMultiLOBPackageWizard, false)}

              <SubmitRow
                lobState={hotelWizardState}
                rfrr="SearchClick_Hotel"
                extraUISTracking={extraTracking}
                globalConfig={globalWizardState.config}
                disabled={
                  currentSubTab === HotelRatesType.PACKAGE_RATES && !hotelWizardState.isPackageRatesTermsChecked
                }
              />

              <HotelHiddenSearchData hotelWizardState={hotelWizardState} />
            </>
          );
        }}
      </MultiLOBPackageWizardContext.Consumer>
    </form>
  );
});

export default WizardHotelPWAv2View;
